"use client";

import { NotificationBell } from "@/components/notifications/NotificationBell";
import { SignInButton, SignedIn, SignedOut, UserButton, useUser, useSupabase } from "@/lib/auth";
import { Menu, X } from "lucide-react";
import Image from "next/image";
import Link from "next/link";
import { usePathname, useRouter } from "next/navigation";
import * as React from "react";

type Role = "user" | "vendor" | "admin";

function isRole(v: unknown): v is Role {
  return v === "user" || v === "vendor" || v === "admin";
}

function useAppRole(): Role | null {
  const { user } = useUser();
  const supabase = useSupabase();
  const [role, setRole] = React.useState<Role | null>(null);

  // fast hint from user metadata (may be stale)
  React.useEffect(() => {
    const meta = user?.unsafeMetadata?.role;
    if (isRole(meta)) setRole(meta);
  }, [user?.unsafeMetadata]);

  // authoritative read from Supabase
  React.useEffect(() => {
    if (!user?.id) return;

    let cancelled = false;
    (async () => {
      try {
        const { data } = await supabase.from("profiles").select("role").eq("id", user.id).maybeSingle();
        const next = (data as { role?: unknown } | null)?.role;
        if (!cancelled && isRole(next)) setRole(next);
      } catch {
        // fail-soft
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [user?.id, supabase]);

  return role;
}

function NavLink({
  href,
  label,
  active,
  onClick,
}: {
  href: string;
  label: string;
  active?: boolean;
  onClick?: () => void;
}) {
  return (
    <Link
      href={href}
      onClick={onClick}
      aria-current={active ? "page" : undefined}
      className={[
        "rounded-full px-3 py-1.5 text-sm font-medium transition-colors",
        active ? "bg-muted text-foreground" : "text-muted-foreground hover:text-foreground hover:bg-muted/40",
      ].join(" ")}
    >
      {label}
    </Link>
  );
}

function RolePill({ role }: { role: Role }) {
  const txt = role.toUpperCase();
  const cls =
    role === "admin"
      ? "bg-[var(--brand,#1D3557)] text-white"
      : role === "vendor"
        ? "bg-slate-900 text-white"
        : "bg-muted text-muted-foreground";

  return <span className={`rounded-full px-2 py-1 text-[11px] font-semibold tracking-wide ${cls}`}>{txt}</span>;
}

export default function Header() {
  const { user } = useUser();
  const role = useAppRole();
  const router = useRouter();
  const pathname = usePathname() || "/";

  const seg = pathname.split("/")[1] ?? "";
  const onAdminRoute = seg === "admin";
  const onVendorRoute = seg === "vendor";

  const effectiveRole: Role | "guest" = role ?? (user ? "user" : "guest");

  const homeHref =
    effectiveRole === "admin"
      ? "/admin/dashboard"
      : effectiveRole === "vendor"
        ? "/vendor/dashboard"
        : user
          ? "/dashboard"
          : "/";

  const goHome = () => router.push(homeHref);

  const nav = onAdminRoute
    ? [
        { href: "/admin/dashboard", label: "Dashboard" },
        { href: "/admin/queue", label: "Queue" },
        { href: "/admin/vendors", label: "Vendors" },
        { href: "/admin/tribute", label: "Tributes" },
        { href: "/admin/bookings", label: "Bookings" },
        { href: "/admin/messages", label: "Messages" },
        { href: "/admin/activity", label: "Activity" },
      ]
    : onVendorRoute
      ? [
          { href: "/vendor/dashboard", label: "Dashboard" },
          { href: "/vendor/bookings", label: "Bookings" },
          { href: "/vendor/messages", label: "Messages" },
          { href: "/vendor/events", label: "Event Plans" },
          { href: "/vendor/profile", label: "Profile" },
        ]
      : effectiveRole === "user"
        ? [
            { href: "/dashboard", label: "Overview" },
            { href: "/tribute", label: "Tributes" },
            { href: "/vendors", label: "Vendors" },
            { href: "/events", label: "Event Plans" },
            { href: "/bookings", label: "Bookings" },
            { href: "/messages", label: "Messages" },
            { href: "/checklist", label: "Checklist" },
          ]
        : [
            { href: "/vendors", label: "Vendors" },
            { href: "/checklist", label: "Checklist" },
            { href: "/tribute", label: "Tribute Pages" },
          ];

  const [open, setOpen] = React.useState(false);
  const toggle = () => setOpen((v) => !v);
  const close = () => setOpen(false);

  const showVendorRibbon = !onAdminRoute && !onVendorRoute && effectiveRole !== "vendor";

  return (
    <header className="sticky top-0 z-50">
      <div className="border-b border-default bg-white/70 backdrop-blur">
        <nav className="container-ely flex items-center justify-between py-3">
          {/* Brand */}
          <button
            type="button"
            onClick={goHome}
            className="flex items-center gap-3 rounded-lg focus:outline-none focus-visible:ring-2 focus-visible:ring-[var(--brand,#1D3557)]"
            aria-label="Home"
          >
            <Image src="/logo.png" alt="Starlit Passage" width={36} height={36} className="rounded-lg" priority />
            <span className="select-none text-lg font-semibold tracking-tight">Starlit Passage</span>
          </button>

          {/* Desktop nav */}
          <div className="hidden items-center gap-1 md:flex">
            {nav.map(({ href, label }) => {
              const active = pathname === href || pathname.startsWith(`${href}/`);
              return <NavLink key={href} href={href} label={label} active={active} />;
            })}
          </div>

          {/* Right rail */}
          <div className="hidden items-center gap-3 md:flex">
            <SignedIn>
              <NotificationBell />
              {role ? <RolePill role={role} /> : null}

              {/* Role jumpers (always available, never intrusive) */}
              {role === "admin" && !onAdminRoute ? (
                <Link
                  href="/admin/dashboard"
                  className="rounded-full border border-border bg-background px-3 py-1.5 text-xs font-semibold text-[var(--brand,#1D3557)] hover:border-[var(--brand,#1D3557)]"
                >
                  Admin →
                </Link>
              ) : null}

              {role === "vendor" && !onVendorRoute ? (
                <Link
                  href="/vendor/dashboard"
                  className="rounded-full border border-border bg-background px-3 py-1.5 text-xs font-semibold text-foreground hover:bg-muted/30"
                >
                  Vendor →
                </Link>
              ) : null}

              <UserButton afterSignOutUrl="/" />
            </SignedIn>

            <SignedOut>
              <SignInButton mode="modal">
                <button
                  type="button"
                  className="rounded-full border border-border bg-background px-4 py-2 text-sm font-semibold text-foreground hover:bg-muted/30"
                >
                  Sign in
                </button>
              </SignInButton>
              <Link
                href="/sign-up"
                className="rounded-full bg-[var(--brand,#1D3557)] px-4 py-2 text-sm font-semibold text-white hover:bg-[var(--brand-2,#16324a)]"
              >
                Start a tribute
              </Link>
            </SignedOut>
          </div>

          {/* Mobile trigger */}
          <div className="flex items-center gap-2 md:hidden">
            <SignedIn>
              <NotificationBell />
            </SignedIn>
            <button
              type="button"
              onClick={toggle}
              className="inline-flex items-center gap-2 rounded-lg px-3 py-2"
              aria-expanded={open}
              aria-controls="sp-mobile-nav"
            >
              <span className="sr-only">Toggle menu</span>
              {open ? <X size={18} /> : <Menu size={18} />}
            </button>
          </div>
        </nav>

        {/* subtle ribbon */}
        {showVendorRibbon ? (
          <div className="border-t border-default bg-white/60">
            <div className="container-ely py-2 text-xs text-muted-foreground">
              Are you a funeral home or provider?{" "}
              <Link href="/sign-up/vendor" className="font-semibold underline underline-offset-4 hover:text-foreground">
                Join the marketplace →
              </Link>
            </div>
          </div>
        ) : null}
      </div>

      {/* Mobile drawer */}
      {open ? (
        <div id="sp-mobile-nav" className="border-b border-default bg-white/95 backdrop-blur md:hidden">
          <div className="container-ely grid gap-2 py-3">
            {nav.map(({ href, label }) => {
              const active = pathname === href || pathname.startsWith(`${href}/`);
              return <NavLink key={href} href={href} label={label} active={active} onClick={close} />;
            })}

            <div className="mt-2 flex flex-col gap-2">
              <SignedOut>
                <SignInButton mode="modal">
                  <button
                    type="button"
                    className="rounded-xl border border-border bg-background px-4 py-2 text-sm font-semibold text-foreground"
                    onClick={close}
                  >
                    Sign in
                  </button>
                </SignInButton>
                <Link
                  href="/sign-up"
                  onClick={close}
                  className="rounded-xl bg-[var(--brand,#1D3557)] px-4 py-2 text-center text-sm font-semibold text-white"
                >
                  Start a tribute
                </Link>
              </SignedOut>

              <SignedIn>
                {role ? (
                  <div className="flex items-center justify-between rounded-xl border border-border bg-muted/20 px-4 py-3">
                    <span className="text-sm font-semibold text-foreground">Signed in</span>
                    <RolePill role={role} />
                  </div>
                ) : null}
                <UserButton afterSignOutUrl="/" />
              </SignedIn>
            </div>
          </div>
        </div>
      ) : null}
    </header>
  );
}
